import { ArrowRightLeft } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * BLU-388: small inline indicator that signals an auto-filled cost was
 * converted from a different currency, with details on hover. Drop-in for
 * any cost cell across the app — but currently wired only at the PO line
 * auto-fill site (Inventory + Products screens display in org base only,
 * per BLU-388 product scope).
 *
 * Renders nothing when no conversion happened (sourceCurrency equals
 * displayCurrency, or no source data) so call sites don't need an
 * is-converted guard.
 *
 * Assumes a parent <TooltipProvider /> exists in the React tree (App.tsx).
 */
interface ConvertedCostIndicatorProps {
  /** The SKU's Default cost, in the org currency (before conversion to the PO currency). */
  sourceAmount: number | null | undefined;
  /** ISO 4217 code of the org currency the Default cost is in. */
  sourceCurrency: string | null | undefined;
  /** ISO 4217 code the value is now displayed in (the PO line currency). */
  displayCurrency: string;
  /**
   * Effective rate applied to convert source to display, expressed as
   * `displayCurrency` per unit of `sourceCurrency`. Optional: if absent,
   * the tooltip shows source amount + currency only.
   */
  rate?: number | null;
  className?: string;
}

const formatSourceAmount = (amount: number): string => {
  // Plain number with 2dp; the source currency is rendered as a separate
  // ISO code label so the locale-aware Intl currency symbol doesn't double
  // with the trailing currency code in the tooltip.
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const ConvertedCostIndicator = ({
  sourceAmount,
  sourceCurrency,
  displayCurrency,
  rate,
  className,
}: ConvertedCostIndicatorProps) => {
  // Same-currency or missing data: render nothing. Caller doesn't need an
  // is-converted check at every site.
  if (sourceAmount == null || !sourceCurrency || sourceCurrency === displayCurrency) {
    return null;
  }

  const rateLabel =
    rate != null && Number.isFinite(rate) && rate > 0
      ? `1 ${sourceCurrency} = ${rate.toFixed(4)} ${displayCurrency}`
      : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-4 w-4 items-center justify-center rounded-sm cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple focus-visible:ring-offset-1 transition-colors',
            'text-threadheads-purple/80 bg-threadheads-purple/10 hover:bg-threadheads-purple/20',
            className,
          )}
          aria-label={`Converted from ${formatSourceAmount(sourceAmount)} ${sourceCurrency}`}
          // Stop click bubble so embedding inside table-row buttons / inputs
          // doesn't trigger row selection or focus jumps.
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowRightLeft className="h-2.5 w-2.5" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="center" className="max-w-xs text-xs">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium font-mono tabular-nums">
            Auto-filled from {formatSourceAmount(sourceAmount)} {sourceCurrency}
          </span>
          {rateLabel && (
            <span className="text-[11px] text-muted-foreground font-mono tabular-nums">{rateLabel}</span>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
